/**
 * Sudoku Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService — never raw alert().
 *
 * Follows ReversiErrorHandler pattern.
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum SudokuErrorType {
  INVALID_MOVE = 'INVALID_MOVE',
  CELL_IS_GIVEN = 'CELL_IS_GIVEN',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  GENERATION_FAILED = 'GENERATION_FAILED',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface SudokuError {
  type: SudokuErrorType;
  message: string;
  details?: unknown;
  timestamp: number;
  recoverable: boolean;
}

export class SudokuErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: SudokuError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: SudokuErrorType,
    message: string,
    details?: unknown,
    showNotification: boolean = true
  ): SudokuError {
    const error: SudokuError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: 'Sudoku', additionalData: details as Record<string, unknown> },
      { showNotification: false }
    );

    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  private classifyError(
    type: SudokuErrorType
  ): { errorType: ErrorType; severity: ErrorSeverity } {
    switch (type) {
      case SudokuErrorType.INVALID_MOVE:
      case SudokuErrorType.CELL_IS_GIVEN:
      case SudokuErrorType.GAME_ENDED:
      case SudokuErrorType.GAME_LOGIC:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };

      case SudokuErrorType.GENERATION_FAILED:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.MEDIUM };

      case SudokuErrorType.CORRUPTED_DATA:
      case SudokuErrorType.SAVE_ERROR:
      case SudokuErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };

      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  // Convenience methods
  handleGameLogicError(reason: string): SudokuError {
    return this.handleError(
      SudokuErrorType.GAME_LOGIC,
      `Game error: ${reason}`,
      { reason }
    );
  }

  handleInvalidMove(row: number, col: number): SudokuError {
    return this.handleError(
      SudokuErrorType.INVALID_MOVE,
      `Invalid move at row ${row + 1}, column ${col + 1}.`,
      { row, col }
    );
  }

  handleCellIsGiven(row: number, col: number): SudokuError {
    return this.handleError(
      SudokuErrorType.CELL_IS_GIVEN,
      'This cell is part of the original puzzle and cannot be changed.',
      { row, col }
    );
  }

  handleCorruptedData(dataType: string, error: unknown): SudokuError {
    return this.handleError(
      SudokuErrorType.CORRUPTED_DATA,
      'Unable to load saved puzzle. Starting new game.',
      { dataType, error },
      true
    );
  }

  handleSaveError(gameId: string, error: unknown): SudokuError {
    return this.handleError(
      SudokuErrorType.SAVE_ERROR,
      'Failed to save puzzle. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  handleLoadError(gameId: string, error: unknown): SudokuError {
    return this.handleError(
      SudokuErrorType.LOAD_ERROR,
      'Failed to load puzzle. Starting new game.',
      { gameId, error },
      true
    );
  }

  // Internal helpers
  private isRecoverable(type: SudokuErrorType): boolean {
    switch (type) {
      case SudokuErrorType.INVALID_MOVE:
      case SudokuErrorType.CELL_IS_GIVEN:
      case SudokuErrorType.GAME_ENDED:
      case SudokuErrorType.GAME_LOGIC:
      case SudokuErrorType.CORRUPTED_DATA:
      case SudokuErrorType.SAVE_ERROR:
      case SudokuErrorType.LOAD_ERROR:
        return true;
      case SudokuErrorType.GENERATION_FAILED:
      default:
        return false;
    }
  }

  private logError(error: SudokuError): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private showErrorNotification(error: SudokuError): void {
    const notificationType = error.recoverable
      ? NotificationType.WARNING
      : NotificationType.ERROR;
    this.notificationService.addNotification({
      type: notificationType,
      title: 'Sudoku Error',
      message: error.message,
      duration: 5000,
    });
  }

  getErrorLog(): SudokuError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
